/**
 * DocsPending — company-wide list of candidates who are currently AT a
 * gate stage AND missing one or more employment documents.
 *
 * - Deep-links each row to the candidate detail page for upload.
 * - "Send nudge" button for one-click candidate email (with cooldown chip).
 * - Empty state if the gate is disabled or all clear.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert, MailWarning, ExternalLink, FileText, Settings } from "lucide-react";

function timeAgo(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const secs = Math.floor((Date.now() - d.getTime()) / 1000);
        if (secs < 60) return "just now";
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    } catch { return iso; }
}

export default function DocsPending() {
    const [state, setState] = useState({ loading: true, items: [], config: null });
    const [q, setQ] = useState("");
    const [nudging, setNudging] = useState({});

    const load = async () => {
        try {
            setState((s) => ({ ...s, loading: true }));
            const { data } = await api.get("/doc-freshness/pending");
            setState({ loading: false, items: data.items || [], config: data.config });
        } catch (err) {
            toast.error(formatApiError(err));
            setState({ loading: false, items: [], config: null });
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase();
        if (!query) return state.items;
        return state.items.filter((r) =>
            (r.candidate_name || "").toLowerCase().includes(query) ||
            (r.candidate_email || "").toLowerCase().includes(query) ||
            (r.gate_applications || []).some((a) => (a.job_title || "").toLowerCase().includes(query))
        );
    }, [state.items, q]);

    const sendNudge = async (cid) => {
        setNudging((m) => ({ ...m, [cid]: true }));
        try {
            const { data } = await api.post(`/candidates/${cid}/doc-freshness/nudge`, {});
            toast.success(`Nudge sent to ${data.sent_to}`);
            // Optimistically update last_nudge_at
            setState((s) => ({
                ...s,
                items: s.items.map((r) => r.candidate_id === cid ? { ...r, last_nudge_at: data.sent_at } : r),
            }));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setNudging((m) => ({ ...m, [cid]: false })); }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="docs-pending-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-bold flex items-center gap-2">
                        <ShieldAlert className="h-7 w-7 text-amber-600" />
                        Docs pending
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Candidates currently at a gate stage {" "}
                        <span className="font-medium">
                            ({(state.config?.gate_stages || []).join(", ") || "—"})
                        </span>{" "}
                        who are missing one or more required employment documents.
                    </p>
                </div>
                <Link to="/company/doc-freshness" className="text-sm text-ikb hover:underline inline-flex items-center gap-1">
                    <Settings className="h-4 w-4" /> Configure
                </Link>
            </div>

            {!state.config?.enabled && !state.loading && (
                <div className="border border-amber-200 bg-amber-50 rounded p-4 text-sm">
                    Doc-freshness gate is currently <strong>disabled</strong>.{" "}
                    <Link to="/company/doc-freshness" className="text-ikb underline">Enable it</Link> to start tracking.
                </div>
            )}

            <Input
                data-testid="docs-pending-search"
                placeholder="Search by name, email, or job title…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="max-w-md"
            />

            {state.loading && <div className="text-sm text-muted-foreground">Loading…</div>}

            {!state.loading && filtered.length === 0 && (
                <div className="border border-dashed border-border rounded p-8 text-center text-muted-foreground text-sm" data-testid="docs-pending-empty">
                    Nothing pending — all candidates at gate stages have their documents in order. 🎉
                </div>
            )}

            <div className="space-y-3">
                {filtered.map((r) => (
                    <div
                        key={r.candidate_id}
                        data-testid={`docs-pending-row-${r.candidate_id}`}
                        className="border border-border rounded p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Link
                                    to={`/candidates/${r.candidate_id}`}
                                    className="font-semibold hover:underline"
                                    data-testid={`docs-pending-name-${r.candidate_id}`}
                                >
                                    {r.candidate_name}
                                </Link>
                                <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                    <FileText className="h-3 w-3" /> {r.missing_count} pending
                                </span>
                                {r.last_nudge_at && (
                                    <span className="text-[11px] text-muted-foreground">
                                        Last nudged {timeAgo(r.last_nudge_at)}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                                {r.candidate_email || "—"}
                            </div>
                            <div className="text-xs mt-2 space-y-1">
                                {(r.gate_applications || []).map((a) => (
                                    <div key={a.application_id}>
                                        <Link to={`/jobs/${a.job_id}`} className="hover:underline">
                                            {a.job_title || "Job"}
                                        </Link>{" "}
                                        · <span className="text-amber-700">{a.stage}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="text-xs text-muted-foreground mt-2">
                                Missing:{" "}
                                {r.missing.map((m, i) => (
                                    <span key={i} className="mr-2">
                                        <span className="font-medium text-foreground">{m.slot_label}</span>{" "}
                                        <span className="italic">({m.exp_label})</span>
                                        {i < r.missing.length - 1 ? "," : ""}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                asChild
                                variant="outline"
                                size="sm"
                                data-testid={`docs-pending-open-${r.candidate_id}`}
                            >
                                <Link to={`/candidates/${r.candidate_id}`}>
                                    <ExternalLink className="h-4 w-4 mr-1" /> Open
                                </Link>
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => sendNudge(r.candidate_id)}
                                disabled={!r.candidate_email || nudging[r.candidate_id]}
                                title={!r.candidate_email ? "No email on file — can't nudge" : "Send an email asking the candidate to upload missing docs"}
                                data-testid={`docs-pending-nudge-${r.candidate_id}`}
                            >
                                <MailWarning className="h-4 w-4 mr-1" />
                                {nudging[r.candidate_id] ? "Sending…" : "Send nudge"}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
